import React, { useEffect, useState } from 'react';
import { TrendingUp, Target, Activity, CheckCircle, BoxIcon } from 'lucide-react';
interface EnhancedMetricCardProps {
  title: string;
  value: string;
  trend: string;
  icon: 'trending' | 'target' | 'activity' | 'check';
  delay?: number;
  accentColor?: string;
}
const iconMap: Record<string, BoxIcon> = {
  trending: TrendingUp,
  target: Target,
  activity: Activity,
  check: CheckCircle
};
/**
 * EnhancedMetricCard component
 * Premium metric card with sparklines, animations, and hover effects
 */
export function EnhancedMetricCard({
  title,
  value,
  trend,
  icon,
  delay = 0,
  accentColor = '#3ab795'
}: EnhancedMetricCardProps) {
  const Icon = iconMap[icon];
  const [isVisible, setIsVisible] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);
  // Generate sparkline data (simple visualization)
  const sparklinePoints = [3, 5, 4, 7, 8, 6, 9, 8, 10];
  const maxPoint = Math.max(...sparklinePoints);
  const sparklinePath = sparklinePoints.map((point, index) => {
    const x = index / (sparklinePoints.length - 1) * 100;
    const y = 100 - point / maxPoint * 100;
    return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ');
  return <div className={`
        relative group
        bg-gradient-to-br from-[#0a0a0a]/80 to-[#0a0a0a]/60
        border border-white/5
        rounded-xl p-5
        transition-all duration-500
        hover:border-[#3ab795]/40
        hover:shadow-[0_8px_24px_rgba(58,183,149,0.15)]
        hover:scale-[1.02]
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
      `} onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)} style={{
    boxShadow: `
          0 1px 3px rgba(0,0,0,0.2),
          0 4px 12px rgba(58,183,149,0.05),
          inset 0 1px 0 rgba(255,255,255,0.03)
        `
  }} data-id="element-201">
      {/* Top accent border */}
      <div className="absolute top-0 left-0 right-0 h-[2px] rounded-t-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{
      background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)`
    }} data-id="element-202" />

      {/* Icon and Trend */}
      <div className="flex items-center justify-between mb-3" data-id="element-203">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 group-hover:scale-110" style={{
        background: `linear-gradient(135deg, ${accentColor}15, ${accentColor}05)`
      }} data-id="element-204">
          <Icon className="w-5 h-5 text-[#3ab795]" strokeWidth={2.5} data-id="element-205" />
        </div>
        <span className="text-sm font-bold text-[#3ab795] flex items-center gap-1" data-id="element-206">
          {trend.startsWith('+') && <TrendingUp className="w-3 h-3" data-id="element-207" />}
          {trend}
        </span>
      </div>

      {/* Value */}
      <div className="space-y-1 mb-3" data-id="element-208">
        <div className="text-3xl font-bold text-white group-hover:text-[#3ab795] transition-colors duration-300" data-id="element-209">
          {value}
        </div>
        <div className="text-xs text-[#b8bcc8] uppercase tracking-wider font-medium" data-id="element-210">
          {title}
        </div>
      </div>

      {/* Sparkline */}
      <div className="h-8 opacity-40 group-hover:opacity-100 transition-opacity duration-300" data-id="element-211">
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full" data-id="element-212">
          <defs data-id="element-213">
            <linearGradient id={`gradient-${title}`} x1="0%" y1="0%" x2="0%" y2="100%" data-id="element-214">
              <stop offset="0%" stopColor={accentColor} stopOpacity="0.3" data-id="element-215" />
              <stop offset="100%" stopColor={accentColor} stopOpacity="0" data-id="element-216" />
            </linearGradient>
          </defs>
          <path d={`${sparklinePath} L 100 100 L 0 100 Z`} fill={`url(#gradient-${title})`} data-id="element-217" />
          <path d={sparklinePath} fill="none" stroke={accentColor} strokeWidth="2" vectorEffect="non-scaling-stroke" data-id="element-218" />
        </svg>
      </div>

      {/* Hover Tooltip */}
      {showTooltip && <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-[#0a0a0a] border border-[#3ab795]/30 rounded-lg px-3 py-2 text-xs text-white whitespace-nowrap z-50 animate-fade-in shadow-lg" data-id="element-219">
          <div className="font-medium" data-id="element-220">{title}</div>
          <div className="text-[#3ab795]" data-id="element-221">{trend} vs last period</div>
          {/* Arrow */}
          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-[#0a0a0a] border-r border-b border-[#3ab795]/30 rotate-45" data-id="element-222" />
        </div>}
    </div>;
}